import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    ListSubheader,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import { logout } from '../../services/authService';

const drawerWidth = 240;

const AdminLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedPath, setSelectedPath] = useState(location.pathname);
    const [userPermissions, setUserPermissions] = useState([]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await apiClient.get('/auth/me');
                const permissions = response.data.role?.permissions || [];
                setUserPermissions(permissions);
            } catch (error) {
                console.error('Fehler beim Laden der Benutzerberechtigungen:', error);
            }
        };

        fetchUserData();
    }, []);

    const hasPermission = (permission) => {
        return userPermissions.some(p => p.name === permission);
    };

    const handleMenuClick = (path) => {
        setSelectedPath(path);
        navigate(path);
    };

    const handleLogout = () => {
        logout();
        setUserPermissions([]);
        navigate('/login');
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Box sx={{ 
                    overflow: 'auto', 
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}>
                    <Box>
                        <List>
                            <ListItem
                                button
                                onClick={() => handleMenuClick('/')}
                            >
                                <ListItemIcon>
                                    <HomeIcon />
                                </ListItemIcon>
                                <ListItemText primary="Zurück zur Website" />
                            </ListItem>
                            <ListItem
                                button
                                selected={selectedPath === '/admin'}
                                onClick={() => handleMenuClick('/admin')}
                            >
                                <ListItemIcon>
                                    <AccountCircleIcon />
                                </ListItemIcon>
                                <ListItemText primary="Profil" />
                            </ListItem>
                            <ListItem
                                button
                                selected={selectedPath === '/admin/password-change'}
                                onClick={() => handleMenuClick('/admin/password-change')}
                            >
                                <ListItemIcon>
                                    <VpnKeyIcon />
                                </ListItemIcon>
                                <ListItemText primary="Passwort ändern" />
                            </ListItem>
                        </List>
                        <Divider />
                        <List
                            subheader={
                                <ListSubheader component="div">
                                    Administration
                                </ListSubheader>
                            }
                        >
                            {hasPermission('can_view_menu_benutzer') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/users'}
                                    onClick={() => handleMenuClick('/admin/users')}
                                >
                                    <ListItemIcon>
                                        <GroupIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Benutzer" />
                                </ListItem>
                            )}
                            {hasPermission('is_landmaster') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/landmaster/users'}
                                    onClick={() => handleMenuClick('/admin/landmaster/users')}
                                >
                                    <ListItemIcon>
                                        <GroupIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Benutzerverwaltung (Landmaster)" />
                                </ListItem>
                            )}
                            {hasPermission('can_view_menu_rights') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/roles'}
                                    onClick={() => handleMenuClick('/admin/roles')}
                                >
                                    <ListItemIcon>
                                        <SecurityIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Rollen & Rechte" />
                                </ListItem>
                            )}
                            {hasPermission('can_view_menu_benutzer') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/indexierung'}
                                    onClick={() => handleMenuClick('/admin/indexierung')}
                                >
                                    <ListItemIcon>
                                        <SearchIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Indexierung" />
                                </ListItem>
                            )}
                        </List>
                        <Divider />
                        <List
                            subheader={
                                <ListSubheader component="div">
                                    Inhalte
                                </ListSubheader>
                            }
                        >
                            {hasPermission('can_view_menu_gericht') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/gerichte'}
                                    onClick={() => handleMenuClick('/admin/gerichte')}
                                >
                                    <ListItemIcon>
                                        <GavelIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Gerichte" />
                                </ListItem>
                            )}
                            {hasPermission('can_view_menu_entscheidung') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/entscheidungen'}
                                    onClick={() => handleMenuClick('/admin/entscheidungen')}
                                >
                                    <ListItemIcon>
                                        <DescriptionIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Entscheidungen" />
                                </ListItem>
                            )}
                            {hasPermission('can_view_menu_mitteilungen') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/mitteilungen'}
                                    onClick={() => handleMenuClick('/admin/mitteilungen')}
                                >
                                    <ListItemIcon>
                                        <NotificationsIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Mitteilungen" />
                                </ListItem>
                            )}
                            {hasPermission('can_view_menu_berufskunde') && (
                                <ListItem
                                    button
                                    selected={selectedPath === '/admin/berufskunde'}
                                    onClick={() => handleMenuClick('/admin/berufskunde')}
                                >
                                    <ListItemIcon>
                                        <DescriptionIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Berufskunde" />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                    <Box sx={{ mt: 'auto' }}>
                        <Divider />
                        <List>
                            <ListItem
                                button
                                onClick={handleLogout}
                                sx={{ color: 'error.main' }}
                            >
                                <ListItemIcon sx={{ color: 'error.main' }}>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText primary="Abmelden" />
                            </ListItem>
                        </List>
                    </Box>
                </Box>
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                {children}
            </Box>
        </Box>
    );
};

export default AdminLayout; 